import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  CircularProgress,
  Chip,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Domain as DomainIcon,
} from '@mui/icons-material';
import { zoullooColors } from '../../theme';
import { useTranslation } from 'react-i18next';
import { getEntityAuditLogs, getUserAuditLogs } from '../../api/audit-logs-api';
import { UserType } from '../../models/UserType';
import { useSearchUsers } from '../../hooks/useUser';
import { UserRole } from '../../models/UserRole';
import { useAuthProvider } from '../../auth/hooks/auth-provider';
import { AuditLog } from '../../models/AuditLog';
import { parseAndFormatAuditJson, getFormattedChanges } from '../../utils/auditValueFormatter';

// Types
interface User {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  type: UserType;
}

interface EntityOption {
  type: string;
  label: string;
}

// Constants - defined outside component to prevent recreating on each render
// Organized by category for better UX
const COMMON_ENTITY_TYPES: EntityOption[] = [
  // Users & Authentication
  { type: 'User', label: '👤 User' },
  { type: 'UserRoleAssignment', label: '👤 User Role Assignment' },
  { type: 'UserSettings', label: '👤 User Settings' },

  // Companies
  { type: 'Company', label: '🏢 Company' },
  { type: 'CompanyAddress', label: '🏢 Company Address' },

  // Clients
  { type: 'Client', label: '🤝 Client' },
  { type: 'ClientExpertise', label: '🤝 Client Expertise' },
  { type: 'ClientBusinessDomain', label: '🤝 Client Business Domain' },
  { type: 'ClientTool', label: '🤝 Client Tool' },

  // Freelancers
  { type: 'Freelancer', label: '💼 Freelancer' },
  { type: 'FreelancerLanguage', label: '💼 Freelancer Language' },
  { type: 'FreelancerProfile', label: '💼 Freelancer Profile' },
  { type: 'FreelancerProfileSkill', label: '💼 Freelancer Profile Skill' },
  { type: 'FreelancerProfileTool', label: '💼 Freelancer Profile Tool' },
  { type: 'FreelancerProfileBusinessDomain', label: '💼 Freelancer Profile Business Domain' },
  { type: 'FreelancerProfileProfessionalExperience', label: '💼 Freelancer Professional Experience' },
  { type: 'FreelancerProfileCompany', label: '💼 Freelancer Company' },
  { type: 'FreelancerProfileCompanyBankAccount', label: '💼 Freelancer Bank Account' },

  // Freelancer Onboarding
  { type: 'FreelancerOnboarding', label: '📋 Freelancer Onboarding' },
  { type: 'FreelancerOnboardingFormsProgress', label: '📋 Freelancer Onboarding Progress' },
  { type: 'FreelancerOnboardingStatusTrack', label: '📋 Freelancer Onboarding Status Track' },
  { type: 'FreelancerOnboardingSoftSkillsEvaluation', label: '📋 Freelancer Soft Skills Evaluation' },

  // Projects
  { type: 'Project', label: '📁 Project' },
  { type: 'ProjectUser', label: '📁 Project User' },
  { type: 'ProjectFreelancerProfile', label: '📁 Project Freelancer Profile' },
  { type: 'ProjectDocument', label: '📁 Project Document' },

  // Proposals
  { type: 'FreelancerProposal', label: '📝 Freelancer Proposal' },
  { type: 'FreelancerProposalTask', label: '📝 Proposal Task' },
  { type: 'FreelancerProposalExpense', label: '📝 Proposal Expense' },
  { type: 'FreelancerProposalExternalCost', label: '📝 Proposal External Cost' },
  { type: 'FreelancerProposalStatusHistory', label: '📝 Proposal Status History' },
  { type: 'FreelancerProposalDocument', label: '📝 Proposal Document' },
  { type: 'ProposalConversation', label: '📝 Proposal Conversation' },

  // Activity Reports
  { type: 'FreelancerActivityReport', label: '📊 Freelancer Activity Report' },

  // Messaging & Notes
  { type: 'ApplicationNote', label: '📨 Application Note' },
  { type: 'ApplicationNoteSubject', label: '📨 Application Note Subject' },

  // Address
  { type: 'Address', label: '📍 Address' },
];

// Tab interface
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`audit-tabpanel-${index}`}
      aria-labelledby={`audit-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AuditLogs: React.FC = () => {
  const { t } = useTranslation();
  const { user:currentUser, isLoading: isUserLoading } = useAuthProvider();

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Entity search state
  const [entityType, setEntityType] = useState<string>('');
  const [entityId, setEntityId] = useState<string>('');
  const [entityLogs, setEntityLogs] = useState<AuditLog[]>([]);
  const [isEntityLoading, setIsEntityLoading] = useState(false);

  // User search state
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userSearchText, setUserSearchText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [userLogs, setUserLogs] = useState<AuditLog[]>([]);
  const [isUserLogLoading, setIsUserLogLoading] = useState(false);
  const [userSearchPerformed, setUserSearchPerformed] = useState(false);

  // Dialog state for viewing log details
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  // User search results - with stable dependency
  const userSearchCriteria = useMemo(() => {
    // Only search if user has typed at least 3 characters
    if (userSearchText.length >= 3) {
      return { email: userSearchText };
    }
    return { email: '' };
  }, [userSearchText]);
  
  const { data: searchedUsers, isLoading: isUserSearchLoading } = useSearchUsers(userSearchCriteria);

  // Handle tab change - with stable callback
  const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  }, []);

  // Entity search - with stable callback
  const handleEntitySearch = useCallback(async () => {
    if (!entityType || !entityId) {
      return;
    }

    setIsEntityLoading(true);
    try {
      const logs = await getEntityAuditLogs(entityType, entityId);
      setEntityLogs(logs);
    } catch (error) {
      console.error('Error fetching entity audit logs:', error);
      setEntityLogs([]);
    } finally {
      setIsEntityLoading(false);
    }
  }, [entityType, entityId]);

  // User search - with stable callback
  const handleUserSearch = useCallback(async () => {
    if (!selectedUser) {
      return;
    }

    setIsUserLogLoading(true);
    setUserSearchPerformed(true);
    try {
      // Convert string dates to Date objects if provided
      const startDateObj = startDate ? new Date(startDate) : undefined;
      const endDateObj = endDate ? new Date(endDate) : undefined;
      
      const logs = await getUserAuditLogs(selectedUser.id, startDateObj, endDateObj);
      setUserLogs(logs);
    } catch (error) {
      console.error('Error fetching user audit logs:', error);
      setUserLogs([]);
    } finally {
      setIsUserLogLoading(false);
    }
  }, [selectedUser, startDate, endDate]);

  // Handle user search text changes
  const handleUserSearchTextChange = useCallback((value: string) => {
    setUserSearchText(value);
    setIsSearching(value.length >= 3);
  }, []);

  // View log details - with stable callback
  const handleViewDetails = useCallback((log: AuditLog) => {
    setSelectedLog(log);
    setDetailsDialogOpen(true);
  }, []);

  // Dialog close handler - with stable callback
  const handleCloseDialog = useCallback(() => {
    setDetailsDialogOpen(false);
  }, []);

  // Format functions moved outside render to prevent recreation
  const formatTimestamp = useCallback((timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  }, []);

  // Get color based on action type
  const getActionColor = useCallback((action: string) => {
    switch (action) {
      case 'Create':
        return zoullooColors.success.main;
      case 'Update':
        return zoullooColors.info.main;
      case 'Delete':
        return zoullooColors.error.main;
      default:
        return zoullooColors.text.primary;
    }
  }, []);

  // Function to safely parse JSON from string
  const safelyParseJSON = useCallback((jsonString: string | null) => {
    if (!jsonString) return null;
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      console.error("Error parsing JSON:", e);
      return null;
    }
  }, []);

  // Check if the user is admin
  if (isUserLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }
  const hasRole = (role: string) => currentUser?.roles?.includes(role);
  
  // Ensure the user is an admin
  if (!hasRole(UserRole.Admin)) {
    return (
      <Box p={4}>
        <Typography variant="h4" color="error">
          {t('error.accessDenied')}
        </Typography>
        <Typography variant="body1" mt={2}>
          This page is only accessible to administrators.
        </Typography>
      </Box>
    );
  }

  // Parse JSON string to object and format it for display with enum labels
  const formatJsonValue = (jsonString: string | null) => {
    if (!jsonString) return null;
    return parseAndFormatAuditJson(jsonString);
  };

  return (
     <Box px={6} py={4}>
      <Typography variant="h2" mb={3}>
        <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        {t('auditLogs.title')}
      </Typography>

      <Paper elevation={3} sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={
            <Box display="flex" alignItems="center">
              <DomainIcon sx={{ mr: 1 }} />
              {t('auditLogs.entitySearch')}
            </Box>
          } />
          <Tab label={
            <Box display="flex" alignItems="center">
              <PersonIcon sx={{ mr: 1 }} />
              {t('auditLogs.userActivity')}
            </Box>
          } />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <Autocomplete
                  freeSolo
                  options={COMMON_ENTITY_TYPES}
                  getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                  value={COMMON_ENTITY_TYPES.find(e => e.type === entityType) || (entityType ? { type: entityType, label: entityType } : null)}
                  onChange={(_, newValue) => {
                    if (typeof newValue === 'string') {
                      setEntityType(newValue);
                    } else {
                      setEntityType(newValue?.type || '');
                    }
                  }}
                  onInputChange={(_, value, reason) => {
                    // Allow free text input for custom entity types
                    if (reason === 'input') {
                      // Check if it matches a known entity type
                      const matchedType = COMMON_ENTITY_TYPES.find(
                        e => e.label.toLowerCase().includes(value.toLowerCase()) || e.type.toLowerCase() === value.toLowerCase()
                      );
                      if (!matchedType && value) {
                        setEntityType(value);
                      }
                    }
                  }}
                  groupBy={(option) => {
                    if (typeof option === 'string') return '';
                    const label = option.label;
                    if (label.startsWith('👤')) return 'Users';
                    if (label.startsWith('🏢')) return 'Companies';
                    if (label.startsWith('🤝')) return 'Clients';
                    if (label.startsWith('💼')) return 'Freelancers';
                    if (label.startsWith('📋')) return 'Onboarding';
                    if (label.startsWith('📁')) return 'Projects';
                    if (label.startsWith('📝')) return 'Proposals';
                    if (label.startsWith('📊')) return 'Reports';
                    if (label.startsWith('📨')) return 'Messaging';
                    return 'Other';
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('auditLogs.entityType')}
                      required
                      placeholder="Project, User, FreelancerProposal..."
                      helperText={t('auditLogs.entityTypeHelper')}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={t('auditLogs.entityId')}
                type="number"
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                required
                placeholder="1, 2, 3..."
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleEntitySearch}
                disabled={!entityType || !entityId || isEntityLoading}
                startIcon={isEntityLoading ? <CircularProgress size={24} /> : <SearchIcon />}
                sx={{
                  backgroundColor: zoullooColors.secondary.main,
                  '&:hover': {
                    backgroundColor: zoullooColors.secondary.dark,
                  },
                  py: 1.5
                }}
              >
                {t('auditLogs.search')}
              </Button>
            </Grid>
          </Grid>

          {entityLogs.length > 0 ? (
            <Box mt={4}>
              <Typography variant="h5" mb={2}>
                {t('auditLogs.entityResults', { type: entityType, id: entityId })}
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('auditLogs.action')}</TableCell>
                      <TableCell>{t('auditLogs.timestamp')}</TableCell>
                      <TableCell>{t('auditLogs.user')}</TableCell>
                      <TableCell>{t('auditLogs.changes')}</TableCell>
                      <TableCell>{t('auditLogs.details')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entityLogs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          <Chip 
                            label={log.action} 
                            sx={{ 
                              backgroundColor: getActionColor(log.action),
                              color: zoullooColors.text.white,
                              fontWeight: 'bold'
                            }} 
                          />
                        </TableCell>
                        <TableCell>{formatTimestamp(log.timestamp)}</TableCell>
                        <TableCell>{log.userEmail || t('auditLogs.system')}</TableCell>
                        <TableCell>
                          {log.changedProperties ? (
                            <Typography variant="body2">
                              {safelyParseJSON(log.changedProperties)?.length || 0} {t('auditLogs.propertiesChanged')}
                            </Typography>
                          ) : (
                            log.action === 'Create' ? t('auditLogs.newEntity') : 
                            log.action === 'Delete' ? t('auditLogs.entityRemoved') : ''
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleViewDetails(log)}
                          >
                            {t('auditLogs.viewDetails')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            entityType && entityId && !isEntityLoading && (
              <Box mt={4} textAlign="center">
                <Typography variant="body1">
                  {t('auditLogs.noRecordsFound')}
                </Typography>
              </Box>
            )
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3} alignItems="flex-start">
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                options={searchedUsers || []}
                getOptionLabel={(option) => 
                  `${option.email} (${option.firstName || ''} ${option.lastName || ''})`
                }
                value={selectedUser}
                onChange={(_, newValue) => {
                  setSelectedUser(newValue);
                  // Reset search state when user changes
                  setUserSearchPerformed(false);
                }}
                onInputChange={(_, value) => {
                  handleUserSearchTextChange(value);
                }}
                loading={isUserSearchLoading && isSearching}
                loadingText={t('auditLogs.searching')}
                noOptionsText={
                  userSearchText.length < 3 
                    ? t('auditLogs.typeAtLeastThreeChars') 
                    : t('auditLogs.noUsersFound')
                }
                filterOptions={(options) => options} // Disable client-side filtering
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('auditLogs.selectUser')}
                    placeholder={t('auditLogs.searchByEmail')}
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isUserSearchLoading && isSearching ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    helperText={t('auditLogs.typeAtLeastThreeChars')}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={t('auditLogs.startDate')}
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={t('auditLogs.endDate')}
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleUserSearch}
                disabled={!selectedUser || isUserLogLoading}
                startIcon={isUserLogLoading ? <CircularProgress size={24} /> : <SearchIcon />}
                sx={{
                  backgroundColor: zoullooColors.secondary.main,
                  '&:hover': {
                    backgroundColor: zoullooColors.secondary.dark,
                  },
                  py: 1.5
                }}
              >
                {t('auditLogs.search')}
              </Button>
            </Grid>
          </Grid>

          {userLogs.length > 0 ? (
            <Box mt={6}>
              <Typography variant="h5" mb={2}>
                {t('auditLogs.userResults', { 
                  name: `${selectedUser?.firstName || ''} ${selectedUser?.lastName || ''}`.trim() || selectedUser?.email 
                })}
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('auditLogs.action')}</TableCell>
                      <TableCell>{t('auditLogs.timestamp')}</TableCell>
                      <TableCell>{t('auditLogs.entityType')}</TableCell>
                      <TableCell>{t('auditLogs.entityId')}</TableCell>
                      <TableCell>{t('auditLogs.changes')}</TableCell>
                      <TableCell>{t('auditLogs.details')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userLogs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          <Chip 
                            label={log.action} 
                            sx={{ 
                              backgroundColor: getActionColor(log.action),
                              color: zoullooColors.text.white,
                              fontWeight: 'bold'
                            }} 
                          />
                        </TableCell>
                        <TableCell>{formatTimestamp(log.timestamp)}</TableCell>
                        <TableCell>{log.entityType}</TableCell>
                        <TableCell>{log.entityId}</TableCell>
                        <TableCell>
                          {log.changedProperties ? (
                            <Typography variant="body2">
                              {safelyParseJSON(log.changedProperties)?.length || 0} {t('auditLogs.propertiesChanged')}
                            </Typography>
                          ) : (
                            log.action === 'Create' ? t('auditLogs.newEntity') : 
                            log.action === 'Delete' ? t('auditLogs.entityRemoved') : ''
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleViewDetails(log)}
                          >
                            {t('auditLogs.viewDetails')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            // Display message only if search was performed and no results found
            selectedUser && userSearchPerformed && !isUserLogLoading && (
              <Box mt={4} textAlign="center">
                <Typography variant="body1">
                  {t('auditLogs.noRecordsFound')}
                </Typography>
              </Box>
            )
          )}
        </TabPanel>
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: zoullooColors.grey[100],
          color: zoullooColors.text.primary
        }}>
          <Typography variant="h5" component="div">
            {t('auditLogs.logDetails')}
            {selectedLog && (
              <Chip 
                label={selectedLog.action} 
                size="small"
                sx={{ 
                  ml: 2,
                  backgroundColor: getActionColor(selectedLog.action),
                  color: zoullooColors.text.white,
                  fontWeight: 'bold'
                }} 
              />
            )}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ 
          backgroundColor: zoullooColors.background.default,
          color: zoullooColors.text.primary
        }}>
          {selectedLog && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('auditLogs.entityInfo')}
                </Typography>
                <Paper sx={{ 
                  p: 2, 
                  mb: 2, 
                  backgroundColor: zoullooColors.background.grey,
                  color: zoullooColors.text.primary
                }}>
                  <Typography variant="body2">
                    <strong>{t('auditLogs.entityType')}:</strong> {selectedLog.entityType}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('auditLogs.entityId')}:</strong> {selectedLog.entityId}
                  </Typography>
                </Paper>

                <Typography variant="subtitle2" color="text.secondary">
                  {t('auditLogs.userInfo')}
                </Typography>
                <Paper sx={{ 
                  p: 2, 
                  mb: 2, 
                  backgroundColor: zoullooColors.background.grey,
                  color: zoullooColors.text.primary
                }}>
                  <Typography variant="body2">
                    <strong>{t('auditLogs.user')}:</strong> {selectedLog.userEmail || t('auditLogs.system')}
                  </Typography>
                  {selectedLog.userId && (
                    <Typography variant="body2">
                      <strong>{t('auditLogs.userId')}:</strong> {selectedLog.userId}
                    </Typography>
                  )}
                </Paper>

                <Typography variant="subtitle2" color="text.secondary">
                  {t('auditLogs.metaInfo')}
                </Typography>
                <Paper sx={{ 
                  p: 2, 
                  backgroundColor: zoullooColors.background.grey,
                  color: zoullooColors.text.primary
                }}>
                  <Typography variant="body2">
                    <strong>{t('auditLogs.timestamp')}:</strong> {formatTimestamp(selectedLog.timestamp)}
                  </Typography>
                  {selectedLog.clientIp && (
                    <Typography variant="body2">
                      <strong>{t('auditLogs.clientIp')}:</strong> {selectedLog.clientIp}
                    </Typography>
                  )}
                  {selectedLog.userAgent && (
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      <strong>{t('auditLogs.userAgent')}:</strong> {selectedLog.userAgent}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('auditLogs.changes')}
                </Typography>

                {selectedLog.changedProperties && (
                  <Paper sx={{ 
                    p: 2, 
                    mb: 2, 
                    backgroundColor: zoullooColors.background.grey,
                    color: zoullooColors.text.primary
                  }}>
                    <Typography variant="body2" fontWeight="bold">
                      {t('auditLogs.changedFields')}:
                    </Typography>
                    {safelyParseJSON(selectedLog.changedProperties)?.map((field: string) => (
                      <Chip 
                        key={field} 
                        label={field} 
                        size="small" 
                        sx={{ m: 0.5 }} 
                      />
                    ))}
                  </Paper>
                )}

                {(selectedLog.oldValues || selectedLog.newValues) && (
                  <Accordion defaultExpanded sx={{ 
                    mb: 2, 
                    backgroundColor: zoullooColors.background.grey,
                    color: zoullooColors.text.primary
                  }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>
                        {selectedLog.action === 'Update' 
                          ? t('auditLogs.beforeAfter') 
                          : selectedLog.action === 'Create' 
                            ? t('auditLogs.newValues')
                            : t('auditLogs.deletedValues')}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {selectedLog.action === 'Update' ? (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>
                                  {t('auditLogs.field')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '35%', backgroundColor: zoullooColors.error.light + '20' }}>
                                  {t('auditLogs.before')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '35%', backgroundColor: zoullooColors.success.light + '20' }}>
                                  {t('auditLogs.after')}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {getFormattedChanges(selectedLog.oldValues, selectedLog.newValues).map((change) => (
                                <TableRow key={change.field}>
                                  <TableCell sx={{ fontWeight: 'medium' }}>{change.field}</TableCell>
                                  <TableCell sx={{
                                    backgroundColor: zoullooColors.error.light + '10',
                                    fontFamily: 'monospace',
                                    fontSize: '0.85rem'
                                  }}>
                                    {change.oldValue}
                                  </TableCell>
                                  <TableCell sx={{
                                    backgroundColor: zoullooColors.success.light + '10',
                                    fontFamily: 'monospace',
                                    fontSize: '0.85rem'
                                  }}>
                                    {change.newValue}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Box
                          component="pre"
                          sx={{
                            p: 2,
                            backgroundColor: zoullooColors.background.disabled,
                            color: zoullooColors.text.primary,
                            overflow: 'auto',
                            fontSize: '0.75rem',
                            borderRadius: 1
                          }}
                        >
                          {formatJsonValue(selectedLog.action === 'Create'
                            ? selectedLog.newValues
                            : selectedLog.oldValues)}
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          backgroundColor: zoullooColors.grey[100],
          color: zoullooColors.text.primary
        }}>
          <Button onClick={handleCloseDialog}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogs;